import { useAuthStore } from '../store/authStore';
import { TrendingUp, Wallet, DollarSign, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  const { user } = useAuthStore();

  const stats = [
    { label: 'Total Staked', value: '$0.00', change: '+0%', positive: true, icon: Wallet },
    { label: 'Total Earnings', value: '$0.00', change: '+0%', positive: true, icon: DollarSign },
    { label: 'Active Positions', value: '0', change: '0 new', positive: true, icon: Activity },
    { label: 'Avg APY', value: '0%', change: '0%', positive: true, icon: TrendingUp },
  ];

  const recentActivity: any[] = [];

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-cream">Welcome back, </span>
            <span className="gradient-text-neon">{user?.firstName || 'User'}!</span>
          </h1>
          <p className="text-sky-blue">Here is an overview of your staking portfolio</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, i) => (
            <div key={i} className="card-premium group">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center group-hover:shadow-neon transition-all">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className={`flex items-center gap-1 text-sm ${stat.positive ? 'text-green-400' : 'text-red-400'}`}>
                  {stat.positive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  <span>{stat.change}</span>
                </div>
              </div>
              <div className="text-3xl font-bold gradient-text-neon mb-1">{stat.value}</div>
              <div className="text-sky-blue text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="card-premium">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-cream">Active Positions</h2>
                <Link to="/staking" className="text-neon-red hover:text-accent-red transition-colors text-sm font-medium">Explore Options →</Link>
              </div>
              {recentActivity.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-steel-blue/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wallet className="w-8 h-8 text-sky-blue" />
                  </div>
                  <h3 className="text-xl font-semibold text-cream mb-2">No Active Positions</h3>
                  <p className="text-sky-blue mb-6">Start staking to see your positions here</p>
                  <Link to="/staking" className="btn-neon inline-block">Browse Staking Options</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((_activity, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-navy-dark/50 rounded-lg border border-sky-blue/10"></div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-premium">
              <h3 className="text-lg font-bold text-cream mb-4">Your Plan</h3>
              <div className="text-center py-6">
                <div className={`inline-block px-4 py-2 rounded-full mb-3 ${user?.subscriptionTier === 'pro' ? 'bg-steel-blue text-white' : user?.subscriptionTier === 'premium' ? 'bg-gradient-cta text-white' : 'bg-sky-blue/20 text-sky-blue'}`}>
                  {user?.subscriptionTier?.toUpperCase() || 'FREE'}
                </div>
                <p className="text-sky-blue text-sm mb-4">
                  {user?.subscriptionTier === 'free' && 'Compare up to 2 protocols'}
                  {user?.subscriptionTier === 'pro' && 'Compare up to 5 protocols'}
                  {user?.subscriptionTier === 'premium' && 'Unlimited comparisons'}
                </p>
                {user?.subscriptionTier === 'free' && (
                  <Link to="/pricing" className="btn-neon block">Upgrade Plan</Link>
                )}
              </div>
            </div>
            <div className="card-premium">
              <h3 className="text-lg font-bold text-cream mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/staking" className="block p-3 bg-steel-blue/20 hover:bg-steel-blue/30 rounded-lg text-sky-blue hover:text-cream transition-all">Browse Staking Options</Link>
                <Link to="/calculator" className="block p-3 bg-steel-blue/20 hover:bg-steel-blue/30 rounded-lg text-sky-blue hover:text-cream transition-all">Calculate Returns</Link>
                <Link to="/portfolio" className="block p-3 bg-steel-blue/20 hover:bg-steel-blue/30 rounded-lg text-sky-blue hover:text-cream transition-all">View Portfolio</Link>
                <Link to="/education" className="block p-3 bg-steel-blue/20 hover:bg-steel-blue/30 rounded-lg text-sky-blue hover:text-cream transition-all">Learn DeFi Staking</Link>
              </div>
            </div>
          </div>
        </div>

        {recentActivity.length === 0 && (
          <div className="mt-8 card-premium">
            <h2 className="text-2xl font-bold text-cream mb-6">Getting Started</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon"><span className="text-white font-bold text-xl">1</span></div>
                <h3 className="text-lg font-semibold text-cream mb-2">Explore Options</h3>
                <p className="text-sky-blue text-sm">Browse 156+ staking protocols and compare APYs, risks, and rewards</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon"><span className="text-white font-bold text-xl">2</span></div>
                <h3 className="text-lg font-semibold text-cream mb-2">Connect Wallet</h3>
                <p className="text-sky-blue text-sm">Link your Web3 wallet with one click to start staking</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon"><span className="text-white font-bold text-xl">3</span></div>
                <h3 className="text-lg font-semibold text-cream mb-2">Start Earning</h3>
                <p className="text-sky-blue text-sm">Stake your crypto and watch your portfolio grow</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
